import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { X, Hash, Users2, Loader2, Check, UserMinus, UserPlus, LogOut, Save, Crown } from "lucide-react";

// Shared dark modal shell matching the app's obsidian/olive language.
function Overlay({ onClose, testid, title, subtitle, icon: Icon, children }) {
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose} data-testid={`${testid}-backdrop`}>
      <div className="w-full max-w-md bg-surface border border-sniper shadow-2xl" data-testid={testid}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-sniper">
          <div className="min-w-0">
            <h3 className="font-heading uppercase tracking-tight text-foreground flex items-center gap-2">
              {Icon && <Icon className="w-4 h-4 text-olive-bright" />} {title}
            </h3>
            {subtitle && <p className="font-mono text-[10px] uppercase text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} data-testid={`${testid}-close`} className="text-muted-foreground hover:text-foreground transition-colors"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-sm text-foreground transition-colors";
const labelCls = "font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5 block";

export function NewChannelModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/team/channels", { name: name.trim(), topic: topic.trim() });
      toast.success(`Channel #${data.name} created.`);
      onCreated?.(data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "Couldn't create channel."); }
    finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose} testid="new-channel-modal" title="New channel" subtitle="Team-wide · everyone on staff can see it" icon={Hash}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className={labelCls}>Channel name</label>
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} data-testid="new-channel-name"
            placeholder="e.g. launch-week" maxLength={40} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Topic <span className="text-muted-foreground/60">(optional)</span></label>
          <input value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="new-channel-topic"
            placeholder="What this channel is for" maxLength={140} className={inputCls} />
        </div>
        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} data-testid="new-channel-cancel" className="font-mono uppercase text-[11px] tracking-wider px-4 py-2 border border-sniper text-muted-foreground hover:text-foreground hover:border-olive transition-colors">Cancel</button>
          <button type="submit" disabled={!name.trim() || busy} data-testid="new-channel-submit"
            className="font-mono uppercase text-[11px] tracking-wider px-4 py-2 bg-olive hover:bg-olive-bright text-white transition-colors disabled:opacity-50 flex items-center gap-1.5">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Hash className="w-3.5 h-3.5" />} Create channel
          </button>
        </div>
      </form>
    </Overlay>
  );
}

export function NewGroupModal({ currentUserId, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]); // [{user_id, username, name, avatar}]
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const MAX_OTHERS = 9; // 10 total including the creator

  // Debounced @handle search across ALL members (server excludes self + blocked accounts).
  useEffect(() => {
    const term = query.trim();
    if (!term) { setResults([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/groups/user-search", { params: { q: term } });
        setResults(data.results || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const isSelected = (uid) => selected.some((s) => s.user_id === uid);
  const addMember = (m) => {
    if (isSelected(m.user_id)) return;
    if (selected.length >= MAX_OTHERS) { toast.error(`A group can have at most ${MAX_OTHERS + 1} people.`); return; }
    setSelected((s) => [...s, m]);
    setQuery(""); setResults([]);
  };
  const removeMember = (uid) => setSelected((s) => s.filter((x) => x.user_id !== uid));

  const submit = async (e) => {
    e.preventDefault();
    if (selected.length < 1 || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/groups", { name: name.trim() || undefined, member_ids: selected.map((s) => s.user_id) });
      toast.success("Group chat created.");
      onCreated?.(data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "Couldn't create group."); }
    finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose} testid="new-group-modal" title="New group chat" subtitle="Private · only the people you pick can see it" icon={Users2}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className={labelCls}>Group name <span className="text-muted-foreground/60">(optional)</span></label>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="new-group-name"
            placeholder="Leave blank to name it after the members" maxLength={60} className={inputCls} />
        </div>

        <div>
          <label className={labelCls}>Add members by @handle · {selected.length}/{MAX_OTHERS} selected</label>
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="new-group-selected">
              {selected.map((m) => (
                <span key={m.user_id} data-testid={`new-group-chip-${m.user_id}`}
                  className="inline-flex items-center gap-1 bg-olive/20 border border-olive/40 text-foreground font-mono text-[11px] px-2 py-1">
                  @{m.username}
                  <button type="button" onClick={() => removeMember(m.user_id)} data-testid={`new-group-chip-remove-${m.user_id}`}
                    className="text-muted-foreground hover:text-blaze transition-colors"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}
          <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="new-group-search"
            placeholder="Search a username…" maxLength={40} className={inputCls} autoComplete="off" />
          {(query.trim() || searching) && (
            <div className="border border-sniper border-t-0 max-h-52 overflow-y-auto overscroll-contain divide-y divide-sniper/60" data-testid="new-group-results">
              {searching && <p className="px-3 py-3 text-xs font-mono text-muted-foreground flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…</p>}
              {!searching && results.length === 0 && <p className="px-3 py-3 text-xs font-mono text-muted-foreground">No members found.</p>}
              {!searching && results.map((m) => {
                const on = isSelected(m.user_id);
                return (
                  <button type="button" key={m.user_id} onClick={() => addMember(m)} data-testid={`new-group-result-${m.user_id}`}
                    disabled={on}
                    className={`w-full text-left flex items-center gap-2.5 px-3 py-2 transition-colors ${on ? "opacity-50" : "hover:bg-white/5"}`}>
                    <span className={`w-4 h-4 shrink-0 border flex items-center justify-center ${on ? "bg-olive border-olive-bright" : "border-sniper"}`}>
                      {on && <Check className="w-3 h-3 text-white" />}
                    </span>
                    <span className="min-w-0">
                      <span className="block font-mono text-xs text-foreground truncate">@{m.username}</span>
                      {m.name && <span className="block font-body text-[10px] text-muted-foreground truncate">{m.name}</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} data-testid="new-group-cancel" className="font-mono uppercase text-[11px] tracking-wider px-4 py-2 border border-sniper text-muted-foreground hover:text-foreground hover:border-olive transition-colors">Cancel</button>
          <button type="submit" disabled={selected.length < 1 || busy} data-testid="new-group-submit"
            className="font-mono uppercase text-[11px] tracking-wider px-4 py-2 bg-olive hover:bg-olive-bright text-white transition-colors disabled:opacity-50 flex items-center gap-1.5">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Users2 className="w-3.5 h-3.5" />} Create group
          </button>
        </div>
      </form>
    </Overlay>
  );
}


// ---------------------------------------------------------------- Manage group (creator controls + leave)
export function GroupManageModal({ gid, onClose, onChanged, onLeft }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [leaving, setLeaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/groups/${gid}`);
      setDetail(data); setName(data.name || "");
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "Couldn't load group."); onClose?.(); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gid]);

  const isCreator = !!detail?.is_creator;
  const memberIds = (detail?.members || []).map((m) => m.user_id);
  const full = (detail?.member_count || 0) >= 10;

  // Debounced @handle search for adding members (creator only, excludes current members).
  useEffect(() => {
    if (!isCreator) return;
    const term = query.trim();
    if (!term) { setResults([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/groups/user-search", { params: { q: term } });
        setResults((data.results || []).filter((r) => !memberIds.includes(r.user_id)));
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, isCreator, detail?.member_count]);

  const saveName = async () => {
    if (savingName) return;
    setSavingName(true);
    try {
      const { data } = await api.patch(`/groups/${gid}`, { name: name.trim() });
      setDetail(data); toast.success("Group renamed."); onChanged?.();
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "Couldn't rename."); }
    finally { setSavingName(false); }
  };

  const addMember = async (uid) => {
    setBusyId(uid);
    try {
      const { data } = await api.post(`/groups/${gid}/members`, { member_ids: [uid] });
      setDetail(data); setQuery(""); setResults([]); toast.success("Member added."); onChanged?.();
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "Couldn't add member."); }
    finally { setBusyId(null); }
  };

  const removeMember = async (uid) => {
    setBusyId(uid);
    try {
      const { data } = await api.delete(`/groups/${gid}/members/${uid}`);
      setDetail(data); toast.success("Member removed."); onChanged?.();
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "Couldn't remove member."); }
    finally { setBusyId(null); }
  };

  const leave = async () => {
    if (leaving) return;
    setLeaving(true);
    try {
      await api.post(`/groups/${gid}/leave`);
      toast.success("You left the group.");
      onLeft?.();
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "Couldn't leave."); setLeaving(false); }
  };

  return (
    <Overlay onClose={onClose} testid="group-manage-modal" title="Manage group"
      subtitle={isCreator ? "You're the group creator" : "Group member"} icon={Users2}>
      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="space-y-5">
          {/* Rename */}
          <div>
            <label className={labelCls}>Group name</label>
            <div className="flex gap-2">
              <input value={name} onChange={(e) => setName(e.target.value)} disabled={!isCreator} maxLength={60}
                data-testid="group-manage-name" placeholder="Named after members when blank"
                className={`${inputCls} disabled:opacity-60 disabled:cursor-not-allowed`} />
              {isCreator && (
                <button onClick={saveName} disabled={savingName || name.trim() === (detail?.name || "").trim()} data-testid="group-manage-name-save"
                  className="shrink-0 font-mono uppercase text-[11px] tracking-wider px-3 bg-olive hover:bg-olive-bright text-white transition-colors disabled:opacity-50 flex items-center gap-1.5">
                  {savingName ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
                </button>
              )}
            </div>
          </div>

          {/* Members */}
          <div>
            <label className={labelCls}>Members · {detail?.member_count || 0}/10</label>
            <div className="border border-sniper divide-y divide-sniper/60 max-h-52 overflow-y-auto overscroll-contain" data-testid="group-manage-members">
              {(detail?.members || []).map((m) => (
                <div key={m.user_id} className="flex items-center gap-2.5 px-3 py-2" data-testid={`group-manage-member-${m.user_id}`}>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5 font-mono text-xs text-foreground truncate">
                      @{m.username}
                      {m.is_creator && <span className="inline-flex items-center gap-1 text-olive-bright font-mono text-[9px] uppercase" title="Creator"><Crown className="w-3 h-3" /> creator</span>}
                    </span>
                    {m.name && <span className="block font-body text-[10px] text-muted-foreground truncate">{m.name}</span>}
                  </span>
                  {isCreator && !m.is_creator && (
                    <button onClick={() => removeMember(m.user_id)} disabled={busyId === m.user_id} data-testid={`group-manage-remove-${m.user_id}`}
                      title="Remove member" className="shrink-0 text-muted-foreground hover:text-blaze transition-colors disabled:opacity-50">
                      {busyId === m.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserMinus className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Add members (creator only) */}
          {isCreator && (
            <div>
              <label className={labelCls}>Add member by @handle</label>
              {full ? (
                <p className="font-mono text-[11px] text-amber-400" data-testid="group-manage-full">Group is full (10 people max).</p>
              ) : (
                <>
                  <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="group-manage-search"
                    placeholder="Search a username…" maxLength={40} autoComplete="off" className={inputCls} />
                  {(query.trim() || searching) && (
                    <div className="border border-sniper border-t-0 max-h-44 overflow-y-auto overscroll-contain divide-y divide-sniper/60" data-testid="group-manage-results">
                      {searching && <p className="px-3 py-3 text-xs font-mono text-muted-foreground flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…</p>}
                      {!searching && results.length === 0 && <p className="px-3 py-3 text-xs font-mono text-muted-foreground">No members found.</p>}
                      {!searching && results.map((m) => (
                        <button type="button" key={m.user_id} onClick={() => addMember(m.user_id)} disabled={busyId === m.user_id}
                          data-testid={`group-manage-add-${m.user_id}`}
                          className="w-full text-left flex items-center gap-2.5 px-3 py-2 hover:bg-white/5 transition-colors disabled:opacity-50">
                          {busyId === m.user_id ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <UserPlus className="w-4 h-4 shrink-0 text-olive-bright" />}
                          <span className="min-w-0">
                            <span className="block font-mono text-xs text-foreground truncate">@{m.username}</span>
                            {m.name && <span className="block font-body text-[10px] text-muted-foreground truncate">{m.name}</span>}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Leave — everyone */}
          <div className="pt-1 border-t border-sniper/60">
            <button onClick={leave} disabled={leaving} data-testid="group-manage-leave"
              className="w-full font-mono uppercase text-[11px] tracking-wider px-4 py-2.5 border border-blaze/40 text-blaze hover:bg-blaze/10 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {leaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5" />} Leave group
            </button>
            {isCreator && (detail?.member_count || 0) > 1 && (
              <p className="mt-1.5 font-mono text-[10px] text-muted-foreground text-center">If you leave, the next member becomes the group creator.</p>
            )}
          </div>
        </div>
      )}
    </Overlay>
  );
}
